import { PASSWORD_POLICY } from '@volee/config'
import { FormField, PasswordInput, RequirementList, type Requirement } from '@volee/ui'
import type { UseFormRegisterReturn } from 'react-hook-form'

import { PASSWORD_RULES, type PasswordRule } from '../data/auth-fields.ts'
import { interpolate, messages } from '../messages/index.ts'

// The password inputs, shared by the flows that ask for one. Presentation only: the
// state belongs to react-hook-form and the placement of errors to data/auth-fields.ts.

export interface PasswordFieldProps {
  readonly label: string
  readonly registration: UseFormRegisterReturn
  readonly error?: string
  readonly pending: boolean
  /** What the browser's password manager is being told. Not decoration: it is the feature. */
  readonly autoComplete: 'current-password' | 'new-password'
  readonly description?: string
  /** False where leaving it empty keeps the password as it is. */
  readonly required?: boolean
}

export function PasswordField({
  label,
  registration,
  error,
  pending,
  autoComplete,
  description,
  required = true,
}: PasswordFieldProps) {
  return (
    <FormField label={label} description={description} error={error} required={required}>
      {(props) => (
        <PasswordInput
          {...props}
          {...registration}
          autoComplete={autoComplete}
          disabled={pending}
          showLabel={messages.password.show}
          hideLabel={messages.password.hide}
        />
      )}
    </FormField>
  )
}

export interface NewPasswordFieldsProps {
  readonly newPassword: UseFormRegisterReturn
  readonly confirmPassword: UseFormRegisterReturn
  readonly errors: { readonly newPassword?: string; readonly confirmPassword?: string }
  readonly pending: boolean
}

/** The pair. The hint mirrors the pool's policy through @volee/config; Cognito validates. */
export function NewPasswordFields({
  newPassword,
  confirmPassword,
  errors,
  pending,
}: NewPasswordFieldsProps) {
  return (
    <>
      <PasswordField
        label={messages.password.new}
        description={interpolate(messages.password.policyHint, {
          minLength: PASSWORD_POLICY.minLength,
        })}
        registration={newPassword}
        error={errors.newPassword}
        pending={pending}
        autoComplete="new-password"
      />
      <PasswordField
        label={messages.password.confirm}
        registration={confirmPassword}
        error={errors.confirmPassword}
        pending={pending}
        autoComplete="new-password"
      />
    </>
  )
}

const RULE_LABELS: Record<PasswordRule, string> = {
  minLength: interpolate(messages.password.ruleMinLength, {
    minLength: PASSWORD_POLICY.minLength,
  }),
  uppercase: messages.password.ruleUppercase,
  lowercase: messages.password.ruleLowercase,
  digit: messages.password.ruleDigit,
}

/** The rules under the pair, each lit as the typed password meets it. */
export function PasswordRequirements({ met }: { readonly met: Record<PasswordRule, boolean> }) {
  const requirements: readonly Requirement[] = PASSWORD_RULES.map((rule) => ({
    label: RULE_LABELS[rule],
    met: met[rule],
  }))
  return <RequirementList label={messages.password.rules} requirements={requirements} />
}
